import type { GameFlow, GameFlowSnapshot } from "../application/game-flow";
import {
  gameplayConfig,
  isModeId,
  type ModeId,
  type TimedModeId,
} from "../config/gameplay-config";
import type { ProgressState } from "../application/progress-state";
import type { DailyChallengeSnapshot } from "../application/daily-challenge-runtime";
import type { SeasonalChallengeHomeSnapshot } from "../application/seasonal-challenge-runtime";

export interface HomeDailyChallengePort {
  getTodaySnapshot(): DailyChallengeSnapshot;
  startMode(mode: ModeId): boolean;
  startToday(): boolean;
}

export interface HomeSeasonalChallengePort {
  getHomeSnapshot(): SeasonalChallengeHomeSnapshot;
  startCurrent(): boolean;
}

export interface HomeProgressPort {
  getState(): Pick<ProgressState, "personalBestLevels">;
}

export interface HomeShellElements {
  readonly backButton: HTMLButtonElement;
  readonly dailyChallengeButton: HTMLButtonElement;
  readonly dailyChallengeStatus: HTMLElement;
  readonly homeScreen: HTMLElement;
  readonly modeButtons: Readonly<Record<ModeId, HTMLButtonElement>>;
  readonly personalBestLabels: Readonly<Record<TimedModeId, HTMLElement>>;
  readonly modeScreen: HTMLElement;
  readonly playButton: HTMLButtonElement;
  readonly playScreen: HTMLElement;
  readonly seasonalChallengeButton: HTMLButtonElement;
  readonly seasonalChallengeStatus: HTMLElement;
}

const modeLabels: Readonly<Record<TimedModeId, string>> = {
  "challenge-1": "1 minute",
  "challenge-5": "5 minutes",
  standard: "3 minutes",
};

const personalBestLabelIds: Readonly<Record<TimedModeId, string>> = {
  "challenge-1": "one-minute-best",
  "challenge-5": "five-minute-best",
  standard: "three-minute-best",
};

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing home shell element: ${id}`);
  }
  return element;
};

const cssColor = (color: number): string =>
  `#${color.toString(16).padStart(6, "0")}`;

export class HomeShell {
  private pickerOpen = false;
  private readonly unsubscribeFlow: () => void;

  public constructor(
    private readonly flow: GameFlow,
    private readonly elements: HomeShellElements,
    private readonly dailyChallenge?: HomeDailyChallengePort,
    private readonly seasonalChallenge?: HomeSeasonalChallengePort,
    private readonly progress?: HomeProgressPort,
  ) {
    elements.playButton.addEventListener("click", this.handlePlay);
    elements.dailyChallengeButton.addEventListener(
      "click",
      this.handleDailyChallenge,
    );
    elements.seasonalChallengeButton.addEventListener(
      "click",
      this.handleSeasonalChallenge,
    );
    elements.backButton.addEventListener("click", this.handleBack);
    for (const mode of gameplayConfig.modes) {
      elements.modeButtons[mode.id].addEventListener("click", this.handleMode);
    }
    this.unsubscribeFlow = flow.subscribe((snapshot) => {
      if (snapshot.scene !== "home") {
        this.pickerOpen = false;
      }
      this.render(snapshot);
    });
  }

  public destroy(): void {
    this.elements.playButton.removeEventListener("click", this.handlePlay);
    this.elements.dailyChallengeButton.removeEventListener(
      "click",
      this.handleDailyChallenge,
    );
    this.elements.seasonalChallengeButton.removeEventListener(
      "click",
      this.handleSeasonalChallenge,
    );
    this.elements.backButton.removeEventListener("click", this.handleBack);
    for (const mode of gameplayConfig.modes) {
      this.elements.modeButtons[mode.id].removeEventListener(
        "click",
        this.handleMode,
      );
    }
    this.unsubscribeFlow();
  }

  private readonly handleBack = (): void => {
    this.pickerOpen = false;
    this.render(this.flow.getSnapshot());
    this.elements.playButton.focus();
  };

  private readonly handlePlay = (): void => {
    if (this.flow.getSnapshot().scene !== "home") {
      return;
    }
    this.pickerOpen = true;
    this.render(this.flow.getSnapshot());
    this.elements.modeButtons["challenge-1"].focus();
  };

  private readonly handleMode = (event: Event): void => {
    const button = event.currentTarget as HTMLButtonElement;
    if (!isModeId(button.value)) {
      throw new Error(`Unsupported mode selection: ${button.value}`);
    }
    this.pickerOpen = false;
    if (this.dailyChallenge === undefined) {
      this.flow.startMode(button.value);
      return;
    }
    this.dailyChallenge.startMode(button.value);
  };

  private readonly handleDailyChallenge = (): void => {
    if (this.flow.getSnapshot().scene !== "home") {
      return;
    }
    this.dailyChallenge?.startToday();
  };

  private readonly handleSeasonalChallenge = (): void => {
    if (this.flow.getSnapshot().scene !== "home") {
      return;
    }
    this.seasonalChallenge?.startCurrent();
  };

  private render(snapshot: GameFlowSnapshot): void {
    const homeVisible = snapshot.scene === "home" && !this.pickerOpen;
    const pickerVisible = snapshot.scene === "home" && this.pickerOpen;
    const gameplayVisible = snapshot.scene === "gameplay";
    this.elements.homeScreen.hidden = !homeVisible;
    this.elements.playScreen.hidden = !pickerVisible;
    this.elements.modeScreen.hidden = !gameplayVisible;
    this.renderDailyChallenge(homeVisible);
    this.renderSeasonalChallenge();
    this.renderPersonalBests();
    if (snapshot.mode === null) {
      this.elements.modeScreen.removeAttribute("data-mode");
      return;
    }
    this.elements.modeScreen.setAttribute("data-mode", snapshot.mode);
  }

  private renderPersonalBests(): void {
    const personalBests = this.progress?.getState().personalBestLevels;
    for (const mode of Object.keys(modeLabels) as TimedModeId[]) {
      const level = personalBests?.[mode] ?? 0;
      const label = this.elements.personalBestLabels[mode];
      label.hidden = level === 0;
      label.textContent = level === 0 ? "" : `Best Level ${String(level)}`;
      const button = this.elements.modeButtons[mode];
      button.setAttribute("aria-label", modeLabels[mode]);
      if (level === 0) {
        button.removeAttribute("aria-describedby");
      } else {
        button.setAttribute("aria-describedby", personalBestLabelIds[mode]);
      }
    }
  }

  private renderDailyChallenge(homeVisible: boolean): void {
    const daily = this.dailyChallenge?.getTodaySnapshot();
    const available = daily !== undefined;
    this.elements.dailyChallengeButton.hidden = !available;
    this.elements.dailyChallengeStatus.hidden = !available;
    if (!available) {
      return;
    }
    this.elements.dailyChallengeButton.dataset.challengeIdentity =
      daily.challenge.identity;
    this.elements.dailyChallengeButton.dataset.completed = String(
      daily.completed,
    );
    this.elements.dailyChallengeStatus.textContent = daily.completed
      ? `Best Level ${String(daily.bestLevel)} · Ready to replay`
      : "1 minute · Same run for everyone";
    if (homeVisible) {
      this.elements.dailyChallengeButton.removeAttribute("aria-hidden");
    }
  }

  private renderSeasonalChallenge(): void {
    const seasonal = this.seasonalChallenge?.getHomeSnapshot();
    const visible =
      seasonal !== undefined &&
      seasonal.descriptor !== null &&
      (seasonal.status === "active" || seasonal.status === "upcoming");
    this.elements.seasonalChallengeButton.hidden = !visible;
    this.elements.seasonalChallengeStatus.hidden = !visible;
    if (!visible) {
      return;
    }
    const active = seasonal.status === "active" && seasonal.challenge !== null;
    this.elements.seasonalChallengeButton.disabled = !active;
    this.elements.seasonalChallengeButton.setAttribute(
      "aria-label",
      seasonal.descriptor.displayName,
    );
    this.elements.seasonalChallengeButton.dataset.seasonalStatus =
      seasonal.status;
    this.elements.seasonalChallengeButton.dataset.seasonalSlug =
      seasonal.descriptor.slug;
    this.elements.seasonalChallengeButton.style.setProperty(
      "--seasonal-primary",
      cssColor(seasonal.descriptor.arena.palette.boundaryColor),
    );
    this.elements.seasonalChallengeButton.style.setProperty(
      "--seasonal-secondary",
      cssColor(seasonal.descriptor.arena.palette.backgroundColor),
    );
    if (seasonal.challenge !== null) {
      this.elements.seasonalChallengeButton.dataset.challengeIdentity =
        seasonal.challenge.identity;
      this.elements.seasonalChallengeButton.dataset.completed = String(
        seasonal.completed,
      );
    } else {
      delete this.elements.seasonalChallengeButton.dataset.challengeIdentity;
      delete this.elements.seasonalChallengeButton.dataset.completed;
    }
    const label = this.elements.seasonalChallengeButton.querySelector("strong");
    if (label !== null) {
      label.textContent = seasonal.descriptor.displayName;
    }
    const icon =
      this.elements.seasonalChallengeButton.querySelector(".snakely-mode-icon");
    if (icon !== null) {
      icon.textContent = seasonal.descriptor.home.icon;
    }
    this.elements.seasonalChallengeStatus.textContent = active
      ? seasonal.completed
        ? `Best Level ${String(seasonal.bestLevel)} · Ready to replay`
        : seasonal.descriptor.home.activeDescription
      : seasonal.descriptor.home.upcomingDescription;
  }
}

export const installHomeShell = (
  documentScope: Document,
  flow: GameFlow,
  dailyChallenge?: HomeDailyChallengePort,
  seasonalChallenge?: HomeSeasonalChallengePort,
  progress?: HomeProgressPort,
): HomeShell =>
  new HomeShell(
    flow,
    {
      backButton: requireElement(
        documentScope,
        "play-back-button",
      ) as HTMLButtonElement,
      dailyChallengeButton: requireElement(
        documentScope,
        "daily-challenge-button",
      ) as HTMLButtonElement,
      dailyChallengeStatus: requireElement(
        documentScope,
        "daily-challenge-status",
      ),
      homeScreen: requireElement(documentScope, "home-screen"),
      modeButtons: {
        "challenge-1": requireElement(
          documentScope,
          "one-minute-button",
        ) as HTMLButtonElement,
        "challenge-5": requireElement(
          documentScope,
          "five-minute-button",
        ) as HTMLButtonElement,
        "free-play": requireElement(
          documentScope,
          "free-play-button",
        ) as HTMLButtonElement,
        standard: requireElement(
          documentScope,
          "three-minute-button",
        ) as HTMLButtonElement,
      },
      modeScreen: requireElement(documentScope, "mode-screen"),
      personalBestLabels: {
        "challenge-1": requireElement(documentScope, "one-minute-best"),
        "challenge-5": requireElement(documentScope, "five-minute-best"),
        standard: requireElement(documentScope, "three-minute-best"),
      },
      playButton: requireElement(
        documentScope,
        "play-button",
      ) as HTMLButtonElement,
      playScreen: requireElement(documentScope, "play-screen"),
      seasonalChallengeButton: requireElement(
        documentScope,
        "seasonal-challenge-button",
      ) as HTMLButtonElement,
      seasonalChallengeStatus: requireElement(
        documentScope,
        "seasonal-challenge-status",
      ),
    },
    dailyChallenge,
    seasonalChallenge,
    progress,
  );
